import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CubeIcon } from '@heroicons/react/24/outline';
import { FaEthereum } from 'react-icons/fa';

const getRarityColor = (rarity) => {
  switch (rarity?.toLowerCase()) {
    case 'legendary':
      return 'from-yellow-500 to-amber-600';
    case 'epic':
      return 'from-purple-500 to-violet-600';
    case 'rare':
      return 'from-blue-500 to-indigo-600';
    case 'uncommon':
      return 'from-green-500 to-emerald-600';
    default:
      return 'from-gray-500 to-gray-600';
  }
};

const getRarityGlow = (rarity) => {
  switch (rarity?.toLowerCase()) {
    case 'legendary':
      return 'shadow-yellow-500/50';
    case 'epic':
      return 'shadow-purple-500/50';
    case 'rare':
      return 'shadow-blue-500/50';
    case 'uncommon':
      return 'shadow-green-500/50';
    default:
      return 'shadow-gray-500/50';
  }
};

export default function NFTDetailsModal({ isOpen, onClose, nft }) {
  if (!nft) return null;

  const rarityColor = getRarityColor(nft.rarity);
  const rarityGlow = getRarityGlow(nft.rarity);

  // Handle both backend and mock data formats
  const nftId = nft._id || nft.id;
  const isMinted = nft.status === 'minted' || nft.status === 'transferred' || nft.status === 'sold' || nft.owner;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-dark-lighter border border-violet-500/20 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-bold leading-6 text-white flex items-center"
                  >
                    <CubeIcon className="h-5 w-5 mr-2 text-violet-500" />
                    NFT Details
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* NFT Image */}
                  <div className="relative">
                    <div className="relative rounded-xl overflow-hidden aspect-square shadow-lg">
                      <img
                        src={nft.image || "/logo.png"}
                        alt={nft.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/logo.png";
                        }}
                      />
                      <div className={`absolute top-4 right-4 text-white px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r ${rarityColor} shadow-lg ${rarityGlow} z-20`}>
                        {nft.rarity}
                      </div>

                      {/* Status badge for minted NFTs */}
                      {nft.status && nft.status !== 'draft' && (
                        <div className="absolute top-4 left-4 bg-green-900 text-green-300 px-3 py-1 rounded-full text-xs font-semibold z-20">
                          {nft.status.charAt(0).toUpperCase() + nft.status.slice(1)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* NFT Details */}
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">{nft.name}</h2>
                    <div className="bg-dark-300 rounded-lg p-4 mb-6 border border-violet-500/10">
                      <h3 className="text-lg font-semibold text-white mb-2">Description</h3>
                      <p className="text-gray-300 whitespace-pre-wrap">{nft.description}</p>
                    </div>

                    {/* Attributes */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-white mb-3">Attributes</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {nft.attributes?.map((attr, index) => (
                          <div key={index} className="bg-dark-300 rounded-lg p-3">
                            <p className="text-gray-400 text-sm">{attr.trait_type}</p>
                            <div className="flex justify-between items-center">
                              <p className="text-white font-semibold">{attr.value}</p>
                              <div className="w-16 h-2 bg-dark-400 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-violet-500 to-purple-600"
                                  style={{ width: `${attr.value}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Price and Action */}
                    <div className="bg-dark-300 rounded-lg p-4 mb-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-gray-400 text-sm">Price</p>
                          <div className="flex items-center text-white text-xl font-bold">
                            <FaEthereum className="mr-1" />
                            <span>{nft.price}</span>
                          </div>
                        </div>
                        <button className="glow-button">
                          {isMinted ? 'Buy Now' : 'Mint Now'}
                        </button>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-dark-300 rounded-lg p-3">
                        <p className="text-gray-400 text-sm">Collection</p>
                        <p className="text-white font-semibold">
                          {nft.collectionId?.name || "BitSport Genesis"}
                        </p>
                      </div>

                      <div className="bg-dark-300 rounded-lg p-3">
                        <p className="text-gray-400 text-sm">Blockchain</p>
                        <p className="text-white font-semibold flex items-center">
                          <CubeIcon className="h-4 w-4 mr-1" /> Immutable X
                        </p>
                      </div>
                    </div>

                    {/* Display image URL for debugging */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="bg-dark-300 rounded-lg p-3 mt-3">
                        <p className="text-gray-400 text-sm">Image URL</p>
                        <p className="text-gray-500 text-xs break-all">
                          {nft.image}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
